import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginUserDto, ResponseCodes, sircapApi } from "shared/api/sircap";
import { startAuthentication } from "@simplewebauthn/browser";
import { PasswordLoginForm } from "widgets/auth/forms";
import { useTranslation } from "react-i18next";
import { sessionModel } from "entities/session";
import { accountStore } from "entities/account";
import { onboardModel, RegisterEnum } from "entities/onboard";
import { PATH_LIST } from "shared/lib/react-router";
import { Capacitor } from "@capacitor/core";
import { useStore } from "zustand";
import { toast } from "react-toastify";
import LogoAndTitle from "../components/logo-title";
import FaceID from "../../../assets/FaceID.svg";
import FaceIDFingerprint from "../../../assets/FaceIDFingerprint.svg";
import Fingerprint from "../../../assets/Fingerprint.svg";
import "./style.scss";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { BioLoadingSpinner } from "widgets/loading";
import { timer } from "helpers/functions";
import IndividualBusinessToggle from "widgets/account-type";
import { sessionStore } from "entities/session/model/sessionModel";

export const LoginPage = () => {
  const { t } = useTranslation("onboarding-auth");
  const { t: o } = useTranslation("onboarding");
  const { t: err } = useTranslation("errors");
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { syncUser, clear } = useStore(onboardModel.onboardStore);
  const { synckyc, synckyb } = useStore(accountStore);
  const { biometric, identificator, setLink, addUser } = useStore(
    sessionModel.sessionStore
  );
  const [bioIsActive, setBioIsActive] = useState(false);
  const [bioCredential, setBioCredential] = useState<{
    username: string;
    password: string;
  } | null>(null);

  const { user } = useStore(sessionStore);

  useEffect(() => {
    if (user) {
      navigate(PATH_LIST.home);
    }

    setLink(PATH_LIST.login);
    return () => setLink("");
  }, []);

  useEffect(() => {
    const logout = sessionStorage.getItem("logoutState");
    if (logout) {
      sessionStorage.removeItem("logoutState");
      navigate(location.pathname, { replace: true });
    } else {
      const platform = Capacitor.getPlatform();
      if (platform === "ios" || platform === "android") {
        if (biometric && identificator) {
          NativeBiometric.getCredentials({
            server: window.location.hostname,
          }).then((value) => {
            if (value) {
              const [_, credId] = value.password.split("-");
              if (biometric === credId) {
                setBioCredential(value);
                setBioIsActive(true);
              }
            }
          });
        }
      } else {
        if (biometric && identificator) {
          setBioIsActive(true);
        }
      }
    }
  }, [biometric, identificator]);

  useEffect(() => {
    if (bioIsActive) handleBiometricsChange();
  }, [bioIsActive]);

  const verifyBio = () => {
    return new Promise<boolean>(async (res) => {
      try {
        await NativeBiometric.verifyIdentity({});
        res(true);
      } catch (error) {
        res(false);
      }
    });
  };

  const handleBiometricsChange = async () => {
    try {
      setIsLoading(true);

      const platform = Capacitor.getPlatform();
      let bioResp = null;

      if (platform === "ios" || platform === "android") {
        const verified = await verifyBio();
        if (!verified) return;

        // const [entity, pinCode] = bioCredential!.password.split("-");
        // const form: LoginUserDto = {
        //   email: bioCredential!.username,
        //   phone: "",
        //   password: pinCode,
        //   entity: entity,
        // };
      } else {
        const response = await sircapApi.auth.loginByBiometric({
          identificator,
        });
        if (response.data) {
          const options = response.data.data;
          bioResp = await startAuthentication(options);
        }
      }

      const resp = await sircapApi.auth.loginByBiometricVerify({
        identificator,
        platform,
        biometric_id: biometric,
        biometric_response: bioResp,
      });

      if (resp.error) {
        switch (resp.error.code) {
          case ResponseCodes.UserIsNotExists:
          case ResponseCodes.WrongEmailOrPassword:
            toast.error(err(String(resp.error.code)));
            break;

          default:
            break;
        }
      } else {
        clear();
        const {
          user,
          accessToken,
          refreshToken,
          entity,
          kyc,
          kyb,
          questionnaireStatus,
          biometrics,
          biometric,
          fcmToken,
          crsData,
          subAccounts,
        } = resp.data.data;

        syncUser(user, entity);
        synckyc(kyc || null);
        synckyb(kyb || null);
        accountStore.setState({ questionnaireStatus });
        addUser(
          user,
          accessToken,
          refreshToken,
          entity,
          biometrics,
          biometric,
          fcmToken,
          crsData,
          subAccounts
        );

        if (user.status !== 1) {
          navigate(PATH_LIST.chooseAccType);
        } else {
          // toast.success("Success login");
          const redirect = sessionStorage.getItem("redirect");
          if (redirect) {
            sessionStorage.removeItem("redirect");
            navigate(redirect);
          } else {
            navigate(PATH_LIST.home);
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login">
      {isLoading && <BioLoadingSpinner />}

      <p className="auth-title">{o("welcomeBack")}</p>

      <div className="login-password-wrapper">
        <PasswordLoginForm />

        <span
          onClick={() => navigate(PATH_LIST.forgotPassword)}
          className="forgot-password-span"
        >
          {t("forgotYourPassword")}
        </span>

        {biometric && identificator && (
          <button
            className="faceID-btn"
            onClick={() => {
              handleBiometricsChange();
            }}
          >
            <img
              style={{
                width: Capacitor.getPlatform() === "ios" ? "36px" : "36px",
              }}
              src={
                Capacitor.getPlatform() === "ios" ? FaceID : FaceIDFingerprint
              }
              alt="FaceID"
            />
          </button>
        )}
      </div>
    </div>
  );
};
